import React, { useState, useEffect } from "react";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

// ─────────────────────────────────────────────────────────────────────────────
//  Intakes — every form in flight, across every bureau.
//
//  This is the funnel the whole phase exists to prove: she adds a family, the
//  family fills the form, a profile appears in the pool. The founder onboards
//  matchmaker #1 by hand and needs to see it without opening each bureau in
//  turn — a bureau with twelve invites and zero confirmations is a phone call.
//
//  The "Nudged" column shows which rungs of the ladder have fired, so a family
//  who has already had two messages is visibly not a candidate for a third.
//
//  CONTACT LOCK: the client's NAME is here and never their number — the server
//  does not send one. The same rule that governs the matchmaker's own Book
//  governs admin.
// ─────────────────────────────────────────────────────────────────────────────

const STATE_MAP = {
  INVITED:   { cls: "bg-gray-100 text-gray-600",   label: "Sent" },
  OPENED:    { cls: "bg-blue-100 text-blue-700",   label: "Opened" },
  PARTIAL:   { cls: "bg-amber-100 text-amber-700", label: "Started" },
  CONFIRMED: { cls: "bg-green-100 text-green-700", label: "Joined" },
  DECLINED:  { cls: "bg-red-100 text-red-600",     label: "Declined" },
  EXPIRED:   { cls: "bg-gray-200 text-gray-500",   label: "Expired" },
  WITHDRAWN: { cls: "bg-gray-200 text-gray-500",   label: "Closed" },
};
const RUNG_LABEL = { d2_client: "client D+2", d5_partner: "bureau D+5" };

const BureauIntakes = () => {
  const [rows, setRows] = useState([]);
  const [stateFilter, setStateFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      const params = { limit: 100 };
      if (stateFilter) params.state = stateFilter;
      try {
        const res = await func.get(urls.PARTNER_INTAKES_URL, { params, signal: controller.signal });
        if (res?.status === 200) setRows(res?.data?.intakes || []);
        else toast.error("Error fetching intakes");
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        toast.error("Network error");
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [stateFilter]);

  return (
    <div className="p-4 sm:p-6">
      <h1 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
        <FontAwesomeIcon icon={faFileLines} className="text-amber-600" />
        Intakes
      </h1>
      <p className="text-sm text-gray-500 mt-1 mb-5">
        Every client form in flight. A bureau with many sent and none joined needs a call, not a metric.
      </p>

      <select
        value={stateFilter}
        onChange={(e) => setStateFilter(e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4"
      >
        <option value="">All states</option>
        {Object.keys(STATE_MAP).filter((k) => k !== "EXPIRED").map((k) => (
          <option key={k} value={k}>{STATE_MAP[k].label}</option>
        ))}
      </select>

      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Bureau</th>
              <th className="text-left px-4 py-3">State</th>
              <th className="text-left px-4 py-3">Reached</th>
              <th className="text-left px-4 py-3">Nudged</th>
              <th className="text-left px-4 py-3">Sent</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && <tr><td colSpan="6" className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>}
            {!loading && !rows.length && (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-gray-400">No forms yet.</td></tr>
            )}
            {!loading && rows.map((r) => {
              const st = STATE_MAP[r.state] || STATE_MAP.INVITED;
              return (
                <tr key={r._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="text-gray-800">{r.name}</div>
                    <div className="text-xs text-gray-400">{r.gender}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {r.bureau ? (r.bureau.bureauName || r.bureau.displayName) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {r.state === "CONFIRMED" ? "6/6" : `${r.lastStep || 0}/6`}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {(r.nudgesSent || []).length
                      ? (r.nudgesSent || []).map((n) => RUNG_LABEL[n] || n).join(", ")
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {r.invitedAt ? new Date(r.invitedAt).toLocaleDateString() : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BureauIntakes;
